import { useCallback } from "react";

import { cn } from "@btnet/utilities-core";

import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import BTLegacyLink from "commonComponents/btWrappers/BTLegacyLink/BTLegacyLink";
import { BTLink, BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import {
    getSearchResultKey,
    type IHighlightedField,
    type ISearchBarResult,
} from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBar.api.types";
import { LinkType } from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBar.utilities";

export const SearchResultCard: React.FunctionComponent<{
    index: number;
    result: ISearchBarResult;
    onResultClick: (result: ISearchBarResult) => void;
    onHover: (i: number) => void;
    active: boolean;
}> = ({ index, result, onResultClick, active, onHover }) => {
    const handleResultItemClick = useCallback(() => {
        onResultClick(result);
    }, [onResultClick, result]);

    const handleHover = useCallback(() => {
        onHover(index);
    }, [index, onHover]);

    const linkProps = {
        to: result.link ?? "",
        className: cn("SearchResult", {
            Active: active,
        }),
        id: getSearchResultKey(index),
        onClick: handleResultItemClick,
        shouldOpenNewTab: result.linkType === LinkType.NewTabLink,
    };
    const searchCardItems = getSearchCardItems(result);

    switch (result.linkType) {
        case LinkType.WebformsLink:
            return (
                <div onMouseMove={handleHover}>
                    <BTLegacyLink {...linkProps}>{searchCardItems}</BTLegacyLink>
                </div>
            );
        case LinkType.NewTabLink:
            return (
                <div onMouseMove={handleHover}>
                    <BTLink {...linkProps}>{searchCardItems}</BTLink>
                </div>
            );
        case LinkType.AutoSPARouting:
            return (
                <div onMouseMove={handleHover}>
                    <BTLink useAutoSPARouting {...linkProps}>
                        {searchCardItems}
                    </BTLink>
                </div>
            );
        case LinkType.RelativeLink:
            return (
                <div onMouseMove={handleHover}>
                    <BTLinkRelative {...linkProps}>{searchCardItems}</BTLinkRelative>
                </div>
            );
        default:
            return <>{searchCardItems}</>;
    }
};

const getSearchCardItems = (result: ISearchBarResult) => {
    return (
        <>
            <div className="SearchResultIcon">{result.icon}</div>
            <div className="SearchResultCard">
                <BTRow align="middle" justify="space-between">
                    <BTCol>
                        <SearchableField
                            className="SearchResultHeader"
                            field={result.primaryField}
                        />
                    </BTCol>
                </BTRow>
                <SearchableField className="SearchResultBody" field={result.secondaryField} />
                <SearchableField className="SearchResultLabel" field={result.tertiaryField} />
            </div>
        </>
    );
};

const SearchableField: React.FunctionComponent<{
    className?: string;
    field?: IHighlightedField;
}> = (props) => {
    const className = cn(props.className, "SearchField");
    if (props.field === undefined) {
        return null;
    }
    if (props.field.hasHighlight && typeof props.field.content === "string") {
        return (
            <div
                className={className}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={
                    props.field?.hasHighlight ? { __html: props.field.content } : undefined
                }
            />
        );
    }
    return <div className={className}>{props.field.content}</div>;
};
